import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Search, X, Printer, Package, FileText, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type ResultType = "consumable" | "variant" | "equipment" | "blog";

interface SearchResult {
  id: string;
  name: string;
  slug: string;
  type: ResultType;
  image_url?: string | null;
  description?: string | null;
  /** parent product name for variants */
  parentName?: string | null;
}

interface SearchGroup {
  type: ResultType;
  items: SearchResult[];
}

/** accent- and case-insensitive fold so "tinta" matches "Tintas" */
const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Field lists must match real columns per table — a non-existent column
// makes PostgREST reject the whole query and that section silently vanishes.
const EQUIPMENT_FIELDS = ["name", "sku", "brand", "mpn", "ean", "short_description", "description", "seo_keywords"];
const CONSUMABLE_FIELDS = ["name", "sku", "ean", "short_description", "description"];
const VARIANT_FIELDS = ["name", "sku", "ean"];
const BLOG_FIELDS = ["title", "excerpt", "content"];

const GROUP_ORDER: ResultType[] = ["consumable", "variant", "equipment", "blog"];

function useGlobalSearchResults(query: string, limits = { equipment: 12, consumables: 12, variants: 12, blog: 5 }) {
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();

  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setGroups([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const tokens = searchQuery.trim().split(/\s+/).filter((t) => t.length >= 2);

      const buildQuery = (q: any, fields: string[]) => {
        for (const token of tokens) {
          const escaped = token.replace(/[%,()]/g, " ");
          const orExpr = fields.map((f) => `${f}.ilike.%${escaped}%`).join(",");
          q = q.or(orExpr);
        }
        return q;
      };

      const [consumablesRes, variantsRes, equipmentRes, blogRes] = await Promise.all([
        buildQuery(
          supabase
            .from("consumables")
            .select("id, name, slug, image_url, short_description")
            .eq("is_active", true),
          CONSUMABLE_FIELDS
        ).limit(limits.consumables),
        buildQuery(
          supabase
            .from("consumable_variants")
            .select("id, name, sku, image_url, consumables(name, slug)")
            .eq("is_active", true),
          VARIANT_FIELDS
        ).limit(limits.variants),
        buildQuery(
          supabase
            .from("equipment")
            .select("id, name, slug, image_url, short_description")
            .eq("is_active", true),
          EQUIPMENT_FIELDS
        ).limit(limits.equipment),
        buildQuery(
          supabase
            .from("blog_articles")
            .select("id, title, slug, featured_image, excerpt")
            .eq("is_published", true),
          BLOG_FIELDS
        ).limit(limits.blog),
      ]);

      const needle = fold(searchQuery.trim());
      // name matches rank above description/sku matches
      const rank = (name: string) => (fold(name).includes(needle) ? 0 : 1);

      const consumables: SearchResult[] = (consumablesRes.data ?? []).map((item: any) => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        type: "consumable" as const,
        image_url: item.image_url,
        description: item.short_description,
      }));

      const variants: SearchResult[] = (variantsRes.data ?? [])
        .filter((v: any) => v.consumables?.slug)
        .map((v: any) => ({
          id: v.id,
          name: v.name,
          slug: v.consumables.slug,
          type: "variant" as const,
          image_url: v.image_url,
          description: null,
          parentName: v.consumables.name,
        }));

      const equipment: SearchResult[] = (equipmentRes.data ?? []).map((item: any) => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        type: "equipment" as const,
        image_url: item.image_url,
        description: item.short_description,
      }));

      const blog: SearchResult[] = (blogRes.data ?? []).map((item: any) => ({
        id: item.id,
        name: item.title,
        slug: item.slug,
        type: "blog" as const,
        image_url: item.featured_image,
        description: item.excerpt,
      }));

      const next: SearchGroup[] = ([
        { type: "consumable", items: consumables.sort((a, b) => rank(a.name) - rank(b.name)) },
        { type: "variant", items: variants.sort((a, b) => rank(a.name) - rank(b.name)) },
        { type: "equipment", items: equipment.sort((a, b) => rank(a.name) - rank(b.name)) },
        { type: "blog", items: blog },
      ] as SearchGroup[]).filter((g) => g.items.length > 0);

      setGroups(next);
    } catch (error) {
      console.error("Search error:", error);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [limits.consumables, limits.variants, limits.equipment, limits.blog]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length >= 2) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => performSearch(query), 300);
    } else {
      setGroups([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  return { groups, isLoading };
}

function getTypeIcon(type: ResultType) {
  switch (type) {
    case "equipment":
      return <Printer className="h-4 w-4 text-primary" />;
    case "consumable":
    case "variant":
      return <Package className="h-4 w-4 text-secondary-foreground" />;
    case "blog":
      return <FileText className="h-4 w-4 text-accent-foreground" />;
  }
}

function getGroupLabel(type: ResultType, t: TFunction) {
  switch (type) {
    case "consumable":
    case "variant":
      return t("nav.consumables", "Consumíveis");
    case "equipment":
      return t("nav.equipment", "Equipamentos");
    case "blog":
      return "Blog";
  }
}

function resultPath(result: SearchResult): string {
  switch (result.type) {
    case "equipment":
      return `/equipamento/${result.slug}`;
    case "consumable":
    case "variant":
      return `/consumivel/${result.slug}`;
    case "blog":
      return `/blog/${result.slug}`;
  }
}

interface ResultRowProps {
  result: SearchResult;
  onClick: () => void;
  selected?: boolean;
  compact?: boolean;
}

function ResultRow({ result, onClick, selected, compact }: ResultRowProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 text-left transition-colors hover:bg-accent",
        compact ? "p-3" : "p-4",
        selected && "bg-accent"
      )}
    >
      <div className={cn("flex-shrink-0 rounded-md overflow-hidden bg-muted", compact ? "w-11 h-11" : "w-14 h-14")}>
        {result.image_url ? (
          <img src={result.image_url} alt={result.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">{getTypeIcon(result.type)}</div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn("font-medium text-foreground truncate", compact ? "text-sm" : "")}>
          {result.parentName ? `${result.parentName} — ${result.name}` : result.name}
        </p>
        {result.description && (
          <p className={cn("text-muted-foreground line-clamp-1 mt-0.5", compact ? "text-xs" : "text-sm")}>
            {result.description}
          </p>
        )}
      </div>
    </button>
  );
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { groups, isLoading } = useGlobalSearchResults(query);

  // flat list for keyboard navigation
  const flat = groups.flatMap((g) => g.items);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setQuery("");
    navigate(resultPath(result));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || flat.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < flat.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flat.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && flat[selectedIndex]) {
          handleResultClick(flat[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const clearSearch = () => {
    setQuery("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  let runningIndex = -1;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="search"
          placeholder={t("search.placeholder", "Pesquisar...")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-[200px] xl:w-[280px] pl-9 pr-8 h-9 bg-muted/50 border-transparent focus:border-primary focus:bg-background transition-all"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 h-9 w-9 hover:bg-transparent"
            onClick={clearSearch}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && query.trim().length >= 2 && (
        <div className="absolute top-full right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 w-[480px] max-w-[calc(100vw-2rem)]">
          {isLoading && groups.length === 0 ? (
            <div className="flex items-center justify-center gap-2 p-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t("search.loading", "A pesquisar...")}</span>
            </div>
          ) : groups.length > 0 ? (
            <div className="max-h-[70vh] overflow-y-auto">
              {groups.map((group) => (
                <div key={group.type}>
                  <div className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    {getTypeIcon(group.type)}
                    {getGroupLabel(group.type, t)}
                  </div>
                  <ul>
                    {group.items.map((result) => {
                      runningIndex += 1;
                      const idx = runningIndex;
                      return (
                        <li key={`${result.type}-${result.id}`}>
                          <ResultRow
                            result={result}
                            compact
                            selected={selectedIndex === idx}
                            onClick={() => handleResultClick(result)}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ) : !isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              <p className="text-sm">{t("search.noResults", "Sem resultados para")} "{query}"</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// Mobile version with full-screen modal
export function GlobalSearchMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { groups, isLoading } = useGlobalSearchResults(query, { equipment: 15, consumables: 15, variants: 15, blog: 6 });

  // Allow other components (e.g. mobile bottom nav) to open search
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener("open-mobile-search", open);
    return () => window.removeEventListener("open-mobile-search", open);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setQuery("");
    navigate(resultPath(result));
  };

  const closeModal = () => {
    setIsOpen(false);
    setQuery("");
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="text-foreground/80 hover:text-primary hover:bg-accent"
        aria-label={t("search.placeholder", "Pesquisar")}
      >
        <Search className="h-5 w-5" />
      </Button>

      {/* Full-screen modal — portaled to body: the header's backdrop-blur/transform
          would otherwise trap the fixed modal inside the translucent header */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[2147483601] bg-background">
          {/* Header */}
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                type="search"
                placeholder={t("search.placeholder", "Pesquisar...")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-11 bg-muted/50 border-transparent focus:border-primary"
                autoComplete="off"
              />
            </div>
            <Button variant="ghost" onClick={closeModal} className="shrink-0">
              {t("common.cancel", "Cancelar")}
            </Button>
          </div>

          {/* Results */}
          <div className="overflow-y-auto h-[calc(100vh-65px)]">
            {isLoading && groups.length === 0 ? (
              <div className="flex items-center justify-center gap-2 p-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t("search.loading", "A pesquisar...")}</span>
              </div>
            ) : query.trim().length >= 2 && groups.length > 0 ? (
              <div>
                {groups.map((group) => (
                  <div key={group.type}>
                    <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                      {getTypeIcon(group.type)}
                      {getGroupLabel(group.type, t)}
                    </div>
                    <ul className="divide-y divide-border">
                      {group.items.map((result) => (
                        <li key={`${result.type}-${result.id}`}>
                          <ResultRow result={result} onClick={() => handleResultClick(result)} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : query.trim().length >= 2 && !isLoading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>{t("search.noResults", "Sem resultados para")} "{query}"</p>
              </div>
            ) : !isLoading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>{t("search.hint", "Digite pelo menos 2 caracteres para pesquisar")}</p>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
